import type { Action, Location, Update } from 'history';
import qs from 'qs';
import type { PropsWithChildren } from 'react';
import {
  useMemo,
  useCallback,
  useDeferredValue,
  useRef,
  useState,
  useTransition,
} from 'react';
import type { ResolveContext } from 'universal-router';
import { useIsomorphicLayoutEffect } from 'usehooks-ts';

import { updateMeta } from '@utils/dom-utils';

import type { RouteContextType } from './router.context';
import { LocationContext, RouteProvider } from './router.context';
import { useNavigator, useRouter } from './router.hooks';
import { useScrollRestoration } from './scroll-restoration';

export const makeRouteResolveContext = ({
  pathname,
  search,
  hash,
}: Location): ResolveContext => ({
  pathname,
  query: qs.parse(search, { ignoreQueryPrefix: true }),
  hash,
});

/**
 * This ia a top-level provider for a Universal Router instance. It manages the
 * current route context and listens for navigation events to update the current
 * location.
 */
export const Router = ({
  children,
  initialRouteContext,
}: PropsWithChildren<{
  initialRouteContext: RouteContextType;
}>) => {
  const [routeContext, setRouteContext] = useState(() => initialRouteContext);

  // Latest resolved pathname, read inside the async resolver to decide whether
  // a location change actually needs re-resolving.
  const pathnameRef = useRef(routeContext.pathname);
  pathnameRef.current = routeContext.pathname;

  const router = useRouter();
  const history = useNavigator();

  const [isPending, startTransition] = useTransition();

  const [{ location, action }, setLocationContext] = useState(() => ({
    location: process.env.BROWSER ? history.location : ({} as Location),
    action: process.env.BROWSER ? history.action : ('' as Action),
  }));

  const context = useMemo(
    () => ({ location, action, isPending }),
    [location, action, isPending],
  );

  const resolveRoute = useCallback(
    async ({ pathname, query, hash }: ResolveContext) => {
      // A query/hash-only change can't alter which route matches, so patch the
      // resolved context's query in place instead of re-resolving. This skips a
      // redundant dynamic import and the re-render churn a full resolve would
      // otherwise trigger on every filter/sort URL update.
      if (pathname === pathnameRef.current) {
        setRouteContext(prev => ({ ...prev, hash, query: query ?? {} }));
        return;
      }

      const newContext = await router.resolve({ pathname, query, hash });

      if (!newContext) return;

      setRouteContext(newContext);

      // Update document metadata
      // TODO: We can use React 19 builtins for this when we upgrade
      document.title = `${newContext.title} - Anchore`;
      updateMeta('description', newContext.description);
    },
    [router],
  );

  const { onNavigate, onRouteCommit } = useScrollRestoration();

  /**
   * Wrap navigation events in a transition so they are handled as deferred
   * state updates.
   */
  const handleLocationChange = useCallback(
    (update: Update) => {
      onNavigate(update);

      startTransition(() => {
        setLocationContext(update);

        resolveRoute(makeRouteResolveContext(update.location));
      });
    },
    [onNavigate, resolveRoute],
  );

  // Deferred to match the route container, which renders the outgoing route for
  // a commit after this one; the destination is only on screen once both agree.
  const committedRouteContext = useDeferredValue(routeContext);

  useIsomorphicLayoutEffect(onRouteCommit, [
    committedRouteContext,
    onRouteCommit,
  ]);

  /**
   * Start listening for navigation events on the client
   */
  useIsomorphicLayoutEffect(
    // A callback to stop listening is returned for cleanup
    () => history.listen(handleLocationChange),
    [history, handleLocationChange],
  );

  return (
    <LocationContext.Provider value={context}>
      <RouteProvider value={routeContext}>{children}</RouteProvider>
    </LocationContext.Provider>
  );
};

export default Router;
